import React, {useRef, useCallback, useState} from 'react'
import styled from 'styled-components'
import {parseCSV} from './utils'
import {CsvRow} from './types'
import {useI18n} from 'i18n/I18nContext'

interface Props {
  file: File | null
  setFile: (f: File | null) => void
  setHeaders: (h: string[]) => void
  setRows: (r: CsvRow[]) => void
  onNext: () => void
}

const Step1Upload: React.FC<Props> = ({
  file,
  setFile,
  setHeaders,
  setRows,
  onNext,
}) => {
  const {t} = useI18n()
  const [isDragging, setIsDragging] = useState(false)
  const [parseError, setParseError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const handlePickFile = useCallback(() => {
    if (fileInputRef.current) fileInputRef.current.click()
  }, [])

  const readFile = useCallback(
    async (f: File) => {
      setParseError(null)
      try {
        const text = await f.text()
        const {headers, rows} = parseCSV(text, {
          emptyHeaderFallback: t('orgCsv.headerUnnamed'),
        })
        if (!headers.length || !rows.length) {
          setParseError(t('orgCsv.errEmpty'))
          return
        }
        setHeaders(headers)
        setRows(rows)
      } catch {
        setParseError(t('orgCsv.errRead'))
      }
    },
    [setHeaders, setRows, t],
  )

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0]
      if (!f) return
      setFile(f)
      await readFile(f)
    },
    [readFile, setFile],
  )

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)
      const f = e.dataTransfer.files?.[0]
      if (f) {
        setFile(f)
        void readFile(f)
      }
    },
    [readFile, setFile],
  )

  return (
    <Wrapper>
      {file && (
        <NextButton className="input__btn" onClick={onNext}>
          {t('orgCsv.next')}
        </NextButton>
      )}

      <Dropzone
        isDragging={isDragging}
        onDragOver={(e) => {
          e.preventDefault()
          setIsDragging(true)
        }}
        onDragLeave={(e) => {
          e.preventDefault()
          setIsDragging(false)
        }}
        onDrop={onDrop}
        onClick={handlePickFile}
      >
        {file ? (
          <>
            <FileName>{file.name}</FileName>
            <FileSize>{(file.size / 1024).toFixed(1)} KB</FileSize>
          </>
        ) : (
          <Placeholder>{t('orgCsv.dropHint')}</Placeholder>
        )}
      </Dropzone>

      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        onChange={handleFileChange}
        style={{display: 'none'}}
      />
      {parseError && <ErrorBox>{parseError}</ErrorBox>}
    </Wrapper>
  )
}

export default Step1Upload

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`
const NextButton = styled.button`
  align-self: flex-end;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`
const Dropzone = styled.div<{isDragging: boolean}>`
  border: 2px dashed ${({isDragging}) => (isDragging ? '#1a73e8' : '#9aa0a6')};
  padding: 40px;
  border-radius: 12px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    background: #f1f3f4;
  }
`
const Placeholder = styled.div`
  color: #5f6368;
  font-size: 16px;
  font-weight: 500;
`
const FileName = styled.div`
  font-weight: 600;
  font-size: 16px;
`
const FileSize = styled.div`
  font-size: 14px;
  color: #5f6368;
  margin-top: 4px;
`
const ErrorBox = styled.div`
  color: #b00020;
  background: #fff5f5;
  border-radius: 8px;
  padding: 8px 12px;
`
